import { useEffect, useState, type ReactNode } from 'react';
import { Link, useParams } from 'react-router-dom';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';

interface AppDetail {
  readonly applicationId: string;
  readonly name: string;
  readonly applicationType: string;
  readonly status: string;
}

interface FormRow {
  readonly formId: string;
  readonly name: string;
  readonly formType: string;
  readonly status: string;
  readonly fieldCount: number;
}

/**
 * GR L28.03 — App Builder (`/lcnc/apps/{id}/builder`). The design surface for a
 * single `lcnc_applications` record: shows the app header and the `lcnc_forms`
 * that make it up so the citizen developer can compose the app. Links to
 * preview and publish.
 */
export function GrAppBuilderPage(): ReactNode {
  const { t } = useTranslation();
  const { token } = useAuth();
  const { id } = useParams<{ id: string }>();
  const [app, setApp] = useState<AppDetail | null>(null);
  const [forms, setForms] = useState<FormRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    Promise.all([
      apiRequest<{ data: AppDetail }>(`/v1/lcnc/applications/${id}`, { token }),
      apiRequest<{ data: FormRow[] }>(`/v1/lcnc/forms?applicationId=${id}`, { token }),
    ])
      .then(([a, f]) => { if (alive) { setApp(a.data); setForms(f.data); setError(null); } })
      .catch((e) => { if (alive) setError((e as Error).message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token, id]);

  return (
    <main aria-label="lcnc-builder">
      {error && <p role="alert">{error}</p>}
      {loading ? <p>{t('gr28.builder.loading')}</p> : app && (
        <>
          <h1 aria-label="app-heading">{t('gr28.builder.heading', { name: app.name, status: app.status })}</h1>
          <p>
            <Link aria-label="open-preview" to={`/lcnc/apps/${id}/preview`}>{t('gr28.builder.preview')}</Link>
            {' · '}
            <Link aria-label="open-publish" to={`/lcnc/apps/${id}/publish`}>{t('gr28.builder.publish')}</Link>
            {' · '}
            <Link aria-label="open-data" to={`/lcnc/apps/${id}/data`}>{t('gr28.builder.data')}</Link>
          </p>
          <p aria-label="form-count">{t('gr28.builder.count', { count: String(forms.length) })}</p>
          {forms.length === 0 ? <p aria-label="empty">{t('gr28.builder.empty')}</p> : (
            <table aria-label="form-table">
              <thead><tr><th>{t('gr28.builder.colForm')}</th><th>{t('gr28.builder.colType')}</th><th>{t('gr28.builder.colStatus')}</th><th>{t('gr28.builder.colFields')}</th></tr></thead>
              <tbody>
                {forms.map((f) => (
                  <tr key={f.formId}>
                    <td>{f.name}</td><td>{f.formType}</td><td>{f.status}</td><td>{f.fieldCount}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </>
      )}
    </main>
  );
}
